import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

import BasicTable from "../../shared/Table";
import { InsightsEmptyState, InsightsTableSkeleton } from "../../Insights/InsightsAsyncState";
import StreetTeamRetailerDialog from "./StreetTeamRetailerDialog";

const linkButtonSx = {
  textTransform: "none",
  justifyContent: "flex-start",
  p: 0,
  minWidth: 0,
  fontWeight: 600,
  color: "#960B10",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
};

function sortRows(items, sortBy, sortDirection) {
  const direction = sortDirection === "asc" ? 1 : -1;
  return [...items].sort((left, right) => {
    if (sortBy === "count") {
      const countDelta = (Number(left.count) || 0) - (Number(right.count) || 0);
      if (countDelta !== 0) {
        return countDelta * direction;
      }
    }
    const leftText = String(sortBy === "detail" ? left.detail : left.label || "");
    const rightText = String(sortBy === "detail" ? right.detail : right.label || "");
    return (
      leftText.localeCompare(rightText, undefined, { sensitivity: "base" }) * direction
    );
  });
}

export default function StreetTeamTable({
  title,
  description,
  nameHeader,
  detailHeader,
  rows = [],
  isLoading = false,
  emptyMessage,
  imageBreakdown = false,
}) {
  const [page, setPage] = useState(0);
  const [selection, setSelection] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("count");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const headers = useMemo(
    () => [
      { id: "label", key: "label", name: nameHeader, disabled: false },
      { id: "detail", key: "detail", name: detailHeader, disabled: false },
      { id: "count", key: "count", name: "Images", disabled: false },
    ],
    [detailHeader, nameHeader]
  );

  const sortedRows = useMemo(
    () => sortRows(rows, sortBy, sortDirection),
    [rows, sortBy, sortDirection]
  );
  const pageRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage).map((row) => {
      const countLabel = Number(row.count || 0).toLocaleString();
      return {
        id: row.id,
        label: row.label,
        detail: row.detail || "—",
        count: imageBreakdown ? (
          <Button
            size="small"
            variant="text"
            sx={linkButtonSx}
            onClick={() =>
              setSelection({
                open: true,
                person: {
                  id: row.id,
                  label: row.label,
                  email: row.detail || "",
                  images: row.images || [],
                },
              })
            }
            aria-label={`Open ${countLabel} ${Number(row.count) === 1 ? "image" : "images"} for ${row.label}`}
          >
            {countLabel}
          </Button>
        ) : (
          countLabel
        ),
      };
    });
  }, [imageBreakdown, page, rowsPerPage, sortedRows]);

  const handleSort = (signedKey) => {
    const nextDirection = String(signedKey).startsWith("-") ? "desc" : "asc";
    const columnKey = String(signedKey).replace(/^-/, "");
    setSortBy(columnKey);
    setSortDirection(nextDirection);
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Typography
        sx={{
          fontWeight: "bold",
          color: "#090909",
          fontSize: "0.9rem",
          textTransform: "capitalize",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontWeight: 300, color: "#090909", fontSize: "0.8rem", mb: 2 }}>
        {description}
      </Typography>

      {isLoading ? <InsightsTableSkeleton /> : null}
      {!isLoading && rows.length === 0 ? <InsightsEmptyState message={emptyMessage} /> : null}
      {!isLoading && rows.length > 0 ? (
        <BasicTable
          headers={headers}
          items={pageRows}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          totalCount={rows.length}
          sortBy={sortBy}
          sortDirection={sortDirection}
          handleSort={handleSort}
          handleChangePage={(_, nextPage) => setPage(nextPage)}
          handleChangeRowsPerPage={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      ) : null}
      {imageBreakdown ? (
        <StreetTeamRetailerDialog
          open={Boolean(selection?.open)}
          mode="uploads"
          retailer={selection?.person || null}
          onClose={() => setSelection((current) => (current ? { ...current, open: false } : null))}
          onExited={() => setSelection(null)}
        />
      ) : null}
    </Box>
  );
}
